import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { ColumnDef } from '@tanstack/react-table';
import { Building2, KeyRound, Plus, ShieldOff, Copy, Smartphone } from 'lucide-react';
import type { GroupList, PageList, User, UserCreate, UserCreated, UserGrants, UserList, UserUpdate } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { DataTable } from '../../components/DataTable';
import { Badge, ConfirmDialog, Field, Modal, PageHeader, SearchableSelect } from '../../components/ui';

type FormState = { email: string; username: string; display_name_en: string; display_name_ar: string; title_en: string; title_ar: string; phone: string; locale: 'en' | 'ar'; groups: string[] };
const empty: FormState = { email: '', username: '', display_name_en: '', display_name_ar: '', title_en: '', title_ar: '', phone: '', locale: 'en', groups: ['staff'] };

export function UsersPage() {
  const { t, i18n } = useTranslation();
  const { can, canAction, me, publicConfig } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<'true' | 'false' | 'all'>('true');
  const [page, setPage] = useState({ page: 1, page_size: 25 });
  const [sort, setSort] = useState('');
  const [editing, setEditing] = useState<User | null | 'new'>(null);
  const [form, setForm] = useState<FormState>(empty);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'reset' | 'revoke' | 'central' | 'totp'; user: User } | null>(null);
  const [grantsFor, setGrantsFor] = useState<User | null>(null);

  const list = useQuery({ queryKey: ['users', { q, active, sort, ...page }], queryFn: () => api.get<UserList>(`/api/users${qs({ q, active, sort, ...page })}`) });
  const groups = useQuery({ queryKey: ['groups'], queryFn: () => api.get<GroupList>('/api/groups') });
  const groupOptions = useMemo(() => (groups.data?.items ?? []).filter((g) => g.is_active).map((g) => ({ value: g.code, label: pick(i18n.language, g.name_en, g.name_ar) })), [groups.data, i18n.language]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['users'] });
  const create = useMutation({
    mutationFn: (body: UserCreate) => api.post<UserCreated>('/api/users', body),
    onSuccess: (res) => {
      toast.success(t('users.created'));
      setEditing(null);
      setTempPassword(res.temporary_password);
      void invalidate();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UserUpdate }) => api.patch<User>(`/api/users/${id}`, body),
    onSuccess: () => {
      toast.success(t('users.updated'));
      setEditing(null);
      void invalidate();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const reset = useMutation({
    mutationFn: (id: string) => api.post<{ temporary_password: string }>(`/api/users/${id}/reset-password`),
    onSuccess: (res) => {
      toast.success(t('users.passwordReset'));
      setTempPassword(res.temporary_password);
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const handOver = useMutation({
    mutationFn: (id: string) => api.post(`/api/users/${id}/hand-to-central`),
    onSuccess: () => { toast.success(t('users.handedOver')); void invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.post(`/api/users/${id}/revoke-sessions`),
    onSuccess: () => toast.success(t('users.sessionsRevoked')),
    onError: (e) => toast.error(describeError(e, t)),
  });
  const resetTotp = useMutation({
    mutationFn: (id: string) => api.post(`/api/users/${id}/totp/reset`),
    onSuccess: () => { toast.success(t('users.totpReset')); void invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });

  const openNew = () => {
    setForm(empty);
    setEditing('new');
  };
  const openEdit = (u: User) => {
    setForm({ email: u.email, username: u.username ?? '', display_name_en: u.display_name_en, display_name_ar: u.display_name_ar, title_en: u.title_en ?? '', title_ar: u.title_ar ?? '', phone: u.phone ?? '', locale: u.locale, groups: u.groups });
    setEditing(u);
  };
  const submit = () => {
    const body = { ...form, username: form.username || null, title_en: form.title_en || null, title_ar: form.title_ar || null, phone: form.phone || null };
    if (editing === 'new') create.mutate(body);
    else if (editing) update.mutate({ id: editing.id, body });
  };

  const columns = useMemo<ColumnDef<User, unknown>[]>(
    () => [
      { id: i18n.language === 'ar' ? 'display_name_ar' : 'display_name_en', header: t('users.nameEn'), accessorFn: (u) => pick(i18n.language, u.display_name_en, u.display_name_ar), cell: ({ row }) => (
          <div>
            <div className="font-medium" dir="auto">{pick(i18n.language, row.original.display_name_en, row.original.display_name_ar)}</div>
            <div className="text-xs text-neutral-500">{row.original.email}{row.original.username ? ` · ${row.original.username}` : ''}</div>
          </div>
        ) },
      { id: 'groups', header: t('users.groups'), enableSorting: false, accessorFn: (u) => u.groups.join(', '), cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.groups.map((g) => <Badge key={g} tone={g === 'admin' ? 'brand' : 'neutral'}>{g}</Badge>)}
            {row.original.auth_provider === 'central' && <Badge tone="info">{t('users.consultantBadge')}</Badge>}
          </div>
        ) },
      { id: 'status', header: t('users.status'), enableSorting: false, accessorFn: (u) => u.is_active, cell: ({ row }) => (
          row.original.locked_until && new Date(row.original.locked_until) > new Date() ? <Badge tone="warn">locked</Badge> : row.original.is_active ? <Badge tone="brand">{t('users.active')}</Badge> : <Badge tone="danger">{t('users.inactive')}</Badge>
        ) },
      { id: 'last_login_at', header: t('users.lastLogin'), accessorFn: (u) => u.last_login_at ?? '', cell: ({ row }) => <span className="text-xs text-neutral-500">{row.original.last_login_at ? new Date(row.original.last_login_at).toLocaleString(i18n.language) : '—'}</span> },
      { id: 'actions', header: t('app.actions'), enableSorting: false, cell: ({ row }) => (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            {canAction('users.reset_password') && row.original.auth_provider === 'local' && (
              <button type="button" className="btn-secondary px-2 py-1" title={t('users.resetPassword')} onClick={() => setConfirm({ kind: 'reset', user: row.original })}><KeyRound size={14} /></button>
            )}
            {canAction('users.reset_password') && row.original.auth_provider === 'local' && row.original.totp_enabled && (
              <button type="button" className="btn-secondary px-2 py-1" title={t('users.resetTotp')} onClick={() => setConfirm({ kind: 'totp', user: row.original })}><Smartphone size={14} /></button>
            )}
            {canAction('users.revoke_sessions') && (
              <button type="button" className="btn-secondary px-2 py-1" title={t('users.revokeSessions')} onClick={() => setConfirm({ kind: 'revoke', user: row.original })}><ShieldOff size={14} /></button>
            )}
            {can('permissions', 'can_edit') && <button type="button" className="btn-secondary px-2 py-1" onClick={() => setGrantsFor(row.original)}>{t('users.grants')}</button>}
            {publicConfig?.login.central && can('users', 'can_edit') && row.original.auth_provider === 'local' && row.original.id !== me?.id && (
              <button type="button" className="btn-secondary px-2 py-1" title={t('users.handToCentralHint')} onClick={() => setConfirm({ kind: 'central', user: row.original })}><Building2 size={14} /></button>
            )}
          </div>
        ) },
    ],
    [t, i18n.language, can, canAction],
  );

  return (
    <div>
      <PageHeader title={t('users.title')} actions={can('users', 'can_create') && <button type="button" className="btn-primary" onClick={openNew}><Plus size={16} /> {t('users.new')}</button>} />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        onRowClick={can('users', 'can_edit') ? openEdit : undefined}
        sort={{ value: sort || (list.data?.meta.sort ?? ''), onChange: (s) => (setSort(s), setPage((p) => ({ ...p, page: 1 }))) }}
        page={list.data ? { ...list.data.meta, onChange: (p, s) => setPage({ page: p, page_size: s }) } : undefined}
        toolbar={
          <>
            <input className="input w-56" placeholder={t('app.search')} value={q} onChange={(e) => (setQ(e.target.value), setPage((p) => ({ ...p, page: 1 })))} />
            <div className="flex overflow-hidden rounded-lg border border-neutral-300 text-xs dark:border-neutral-700">
              {(['true', 'false', 'all'] as const).map((v) => (
                <button key={v} type="button" onClick={() => (setActive(v), setPage((p) => ({ ...p, page: 1 })))} className={`px-2 py-1 ${active === v ? 'bg-brand-600 text-white' : 'bg-white dark:bg-neutral-900'}`}>
                  {t(v === 'true' ? 'users.filterActive' : v === 'false' ? 'users.filterInactive' : 'users.filterAll')}
                </button>
              ))}
            </div>
          </>
        }
      />

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? t('users.new') : t('users.editTitle')}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>{t('app.cancel')}</button>
            <button type="button" className="btn-primary" onClick={submit} disabled={create.isPending || update.isPending}>{t('app.save')}</button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('users.email')} required><input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={editing !== 'new' && editing?.auth_provider === 'central'} /></Field>
          <Field label={t('users.username')}><input className="input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></Field>
          <Field label={t('users.nameEn')} required><input className="input" value={form.display_name_en} onChange={(e) => setForm({ ...form, display_name_en: e.target.value })} /></Field>
          <Field label={t('users.nameAr')} required><input className="input" dir="rtl" value={form.display_name_ar} onChange={(e) => setForm({ ...form, display_name_ar: e.target.value })} /></Field>
          <Field label={t('users.titleEn')}><input className="input" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} /></Field>
          <Field label={t('users.titleAr')}><input className="input" dir="rtl" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} /></Field>
          <Field label={t('users.phone')}><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label={t('users.locale')}><SearchableSelect value={form.locale} onChange={(v) => setForm({ ...form, locale: v as 'en' | 'ar' })} options={[{ value: 'en', label: 'English' }, { value: 'ar', label: 'العربية' }]} /></Field>
          <div className="sm:col-span-2">
            <Field label={t('users.groups')}><SearchableSelect multiple value={form.groups} onChange={(v) => setForm({ ...form, groups: v as string[] })} options={groupOptions} /></Field>
          </div>
          {editing !== 'new' && editing && editing.id !== me?.id && (
            <div className="sm:col-span-2">
              <button type="button" className={editing.is_active ? 'btn-danger' : 'btn-primary'} onClick={() => update.mutate({ id: editing.id, body: { is_active: !editing.is_active } })}>
                {editing.is_active ? t('users.deactivate') : t('users.activate')}
              </button>
            </div>
          )}
        </div>
      </Modal>

      <Modal open={tempPassword !== null} onClose={() => setTempPassword(null)} title={t('users.temporaryPassword')} footer={<button type="button" className="btn-primary" onClick={() => setTempPassword(null)}>{t('app.close')}</button>}>
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">{t('users.temporaryPasswordHint')}</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 rounded-lg bg-neutral-100 px-3 py-2 font-mono text-lg tracking-wider dark:bg-neutral-800" dir="ltr">{tempPassword}</code>
          <button type="button" className="btn-secondary" onClick={() => { void navigator.clipboard.writeText(tempPassword ?? ''); toast.success(t('app.copied')); }}><Copy size={16} /></button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'reset' ? t('users.resetPassword') : confirm?.kind === 'central' ? t('users.handToCentral') : confirm?.kind === 'totp' ? t('users.resetTotp') : t('users.revokeSessions')}
        body={confirm ? `${pick(i18n.language, confirm.user.display_name_en, confirm.user.display_name_ar)}${confirm.kind === 'central' ? ` — ${t('users.handToCentralHint')}` : confirm.kind === 'totp' ? ` — ${t('users.resetTotpHint')}` : ''}` : undefined}
        danger
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'reset') reset.mutate(confirm.user.id);
          else if (confirm.kind === 'totp') resetTotp.mutate(confirm.user.id);
          else if (confirm.kind === 'central') handOver.mutate(confirm.user.id);
          else revoke.mutate(confirm.user.id);
          setConfirm(null);
        }}
      />

      {grantsFor && <GrantsModal user={grantsFor} onClose={() => setGrantsFor(null)} />}
    </div>
  );
}

function GrantsModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const pages = useQuery({ queryKey: ['pages'], queryFn: () => api.get<PageList>('/api/pages') });
  const grants = useQuery({ queryKey: ['user-grants', user.id], queryFn: () => api.get<UserGrants>(`/api/users/${user.id}/grants`) });
  const [state, setState] = useState<Record<string, { can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean }> | null>(null);
  const current = state ?? Object.fromEntries((grants.data?.items ?? []).map((g) => [g.page_code, { can_view: g.can_view, can_create: g.can_create, can_edit: g.can_edit, can_delete: g.can_delete }]));
  const save = useMutation({
    mutationFn: () => api.put(`/api/users/${user.id}/grants`, { pages: Object.entries(current).filter(([, f]) => f.can_view).map(([page_code, f]) => ({ page_code, ...f })) }),
    onSuccess: () => {
      toast.success(t('users.grantsSaved'));
      onClose();
    },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const toggle = (code: string, key: 'can_view' | 'can_create' | 'can_edit' | 'can_delete') => {
    const row = current[code] ?? { can_view: false, can_create: false, can_edit: false, can_delete: false };
    const next = { ...row, [key]: !row[key] };
    if (key !== 'can_view' && next[key]) next.can_view = true;
    if (key === 'can_view' && !next.can_view) Object.assign(next, { can_create: false, can_edit: false, can_delete: false });
    setState({ ...current, [code]: next });
  };
  return (
    <Modal open onClose={onClose} title={`${t('users.grants')} · ${pick(i18n.language, user.display_name_en, user.display_name_ar)}`} wide footer={<><button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button><button type="button" className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>{t('app.save')}</button></>}>
      <p className="mb-3 text-sm text-neutral-500">{t('users.grantsHint')}</p>
      <table className="w-full text-sm">
        <thead><tr className="text-xs uppercase text-neutral-500"><th className="py-1 text-start">{t('permissions.pages')}</th>{(['view', 'create', 'edit', 'delete'] as const).map((k) => <th key={k} className="py-1">{t(`permissions.${k}`)}</th>)}</tr></thead>
        <tbody>
          {(pages.data?.items ?? []).map((p) => (
            <tr key={p.code} className="border-t border-neutral-100 dark:border-neutral-800">
              <td className="py-1" dir="auto">{pick(i18n.language, p.name_en, p.name_ar)} <span className="text-xs text-neutral-400">{p.code}</span></td>
              {(['can_view', 'can_create', 'can_edit', 'can_delete'] as const).map((k) => <td key={k} className="py-1 text-center"><input type="checkbox" checked={Boolean(current[p.code]?.[k])} onChange={() => toggle(p.code, k)} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}
